/**
 * Phase 27b — small hook that reads `tenant.runtime_engines` from
 * `/api/tenant/me` and merges with the same defaults the backend
 * applies (see `app/models.py::DEFAULT_RUNTIME_ENGINES`). Missing
 * keys default to `true`, so a brand-new tenant always shows Phase 27
 * surfaces until an admin explicitly opts out.
 *
 * Cached at module scope for the session — one fetch per full-page
 * load, no polling. Exposed as a hook so components can read the flags
 * without prop drilling.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const DEFAULTS = { pto: true, ot: true, break: true, schedule_notify: true };

let cache = null;
let inflight = null;

function normalize(raw) {
  const out = { ...DEFAULTS };
  if (raw && typeof raw === "object") {
    for (const k of Object.keys(DEFAULTS)) {
      if (typeof raw[k] === "boolean") out[k] = raw[k];
    }
  }
  return out;
}

async function fetchOnce() {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = api
    .get("/tenant/me")
    .then((r) => {
      cache = normalize(r.data?.runtime_engines);
      inflight = null;
      return cache;
    })
    .catch(() => {
      cache = { ...DEFAULTS };
      inflight = null;
      return cache;
    });
  return inflight;
}

export function invalidateRuntimeEngines() {
  cache = null;
}

export function useRuntimeEngines() {
  const [flags, setFlags] = useState(cache || DEFAULTS);
  const [ready, setReady] = useState(cache !== null);

  useEffect(() => {
    let cancelled = false;
    fetchOnce().then((f) => {
      if (!cancelled) {
        setFlags(f);
        setReady(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return { ...flags, ready };
}
